import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import {
  CompanyScheduleService,
  type CompanySchedule,
  type CompanyScheduleUiState,
  type CompanyScheduleUpsertPayload,
} from './companies/company-schedule.service';

/**
 * Misma shape que empresas (`attention` / `payments` / `holidays`).
 * GET/PUT `/api/structure/branches/{branchId}/schedules`
 */
export type BranchSchedule = CompanySchedule & { branchId?: string };
export type BranchScheduleUpsertPayload = CompanyScheduleUpsertPayload;
export type BranchScheduleUiState = CompanyScheduleUiState;

/**
 * Sub-recurso de horarios de sucursal.
 * Reutiliza el mapper de empresas (misma estructura API).
 */
@Injectable({ providedIn: 'root' })
export class BranchScheduleService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly scheduleMapper = inject(CompanyScheduleService);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/branches`;
  }

  get(branchId: string): Observable<BranchSchedule | null> {
    return this.httpClient
      .get<ApiResponse<BranchSchedule | null>>(
        `${this.apiUrl}/${branchId}/schedules`,
      )
      .pipe(map((response) => response.data ?? null));
  }

  update(
    branchId: string,
    payload: BranchScheduleUpsertPayload,
  ): Observable<BranchSchedule | null> {
    return this.httpClient
      .put<ApiResponse<BranchSchedule | null>>(
        `${this.apiUrl}/${branchId}/schedules`,
        payload,
      )
      .pipe(map((response) => response.data ?? null));
  }

  toUiState(schedule: BranchSchedule | null): BranchScheduleUiState {
    return this.scheduleMapper.toUiState(schedule);
  }

  toUpsertPayload(ui: BranchScheduleUiState): BranchScheduleUpsertPayload {
    return this.scheduleMapper.toUpsertPayload(ui);
  }
}
